import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useSession } from '@/hooks/useSession';
import {
  arrangeSeats,
  crossedMilestone,
  describeChange,
  describeMilestone,
  diffSeating,
  focusedMinutes,
  seatsByUid,
  usePresence,
} from '@/hooks/usePresence';
import { activeScene } from '@/data/scenes';
import { getWorld } from '@/data/worlds';
import { getLandingPage } from '@/seo/pages';
import Lobby from '@/components/lobby/Lobby';
import World from '@/components/world/World';
import WorldRenderer from '@/components/world/WorldRenderer';
import SceneRenderer from '@/components/world/SceneRenderer';
import SessionComplete from '@/components/postcard/SessionComplete';
import { playChime } from '@/audio/chime';
import { cityOf, myTimezone, whereLabel } from '@/lib/place';

interface YouTubePlayer {
  cueVideoById: (id: string, startSeconds?: number, quality?: string) => void;
  loadVideoById: (id: string, startSeconds?: number, quality?: string) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  setVolume: (volume: number) => void;
  getPlayerState: () => number;
}

interface AppProps {
  player?: YouTubePlayer | null;
  loadSong?: (mode: string) => void;
}

const BACKDROP_IMAGES = [
  '/backgrounds/bg.jpeg',
  '/backgrounds/bg2.jpg',
  '/backgrounds/bg3.jpg',
];

type StagePhase = 'lobby' | 'entering' | 'world' | 'exiting' | 'complete';

const LOOK_KEY = 'kf-look';
const CHIME_KEY = 'kf-chime';
const SHARE_PLACE_KEY = 'kf-share-place';
const CHIME_GAP_MS = 60_000; // several arrivals in a row make one chime

// localStorage can throw (private mode, blocked storage); these are optional
const readStored = (key: string) => {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};
const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not persisted; still applies for this visit
  }
};

const App: React.FC<AppProps> = ({ player, loadSong }) => {
  const { worldId } = useParams<{ worldId?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const session = useSession();
  const landingPage = getLandingPage(location.pathname);

  // Random backdrop image (stable per visit)
  const backdropImage = useMemo(
    () => BACKDROP_IMAGES[Math.floor(Math.random() * BACKDROP_IMAGES.length)],
    []
  );

  // Handle /enter/:worldId route with optional query params
  useEffect(() => {
    if (worldId) {
      const world = getWorld(worldId);
      if (world) {
        session.selectWorld(world);
      }

      const duration = searchParams.get('d');
      const task = searchParams.get('t');

      if (duration) {
        const d = parseInt(duration, 10);
        if (!isNaN(d) && d >= 5 && d <= 120) {
          session.setDuration(d);
        }
      }

      if (task) {
        session.setTask(decodeURIComponent(task));
      }
    }
  }, [worldId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SEO landing pages (e.g. /timer/45-minutes) open the lobby with their preset
  useEffect(() => {
    if (!landingPage) return;
    const { duration, musicMode } = landingPage.preset;
    if (duration) session.setDuration(duration);
    if (musicMode) session.setMusicMode(musicMode);
  }, [landingPage?.path]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ReactGA.send('pageview');
  }, []);

  const phase: StagePhase = session.isComplete
    ? 'complete'
    : session.isTransitioning
      ? 'entering'
      : session.isInWorld
        ? 'world'
        : session.isExiting
          ? 'exiting'
          : 'lobby';

  // Who "you" are in the scene, picked in the lobby and remembered
  const [lookId, setLookId] = useState(() => readStored(LOOK_KEY));
  const pickLook = (id: string) => {
    setLookId(id);
    store(LOOK_KEY, id);
  };

  // A quiet chime when someone sits down; on unless turned off
  const [chimeOn, setChimeOn] = useState(() => readStored(CHIME_KEY) !== 'off');
  const chimeOnRef = useRef(chimeOn);
  chimeOnRef.current = chimeOn;
  const lastChimeRef = useRef(0);
  // Your timezone (shown as "3pm in London") is shared unless you hide it
  const [sharePlace, setSharePlace] = useState(() => readStored(SHARE_PLACE_KEY) !== 'off');
  const toggleSharePlace = () => {
    setSharePlace((on) => {
      store(SHARE_PLACE_KEY, on ? 'off' : 'on');
      return !on;
    });
  };
  const myTz = useMemo(myTimezone, []);

  const toggleChime = () => {
    setChimeOn((on) => {
      store(CHIME_KEY, on ? 'off' : 'on');
      return !on;
    });
  };

  // Seconds focused this session; the timer only counts down while running,
  // so breaks are excluded
  const focusedSeconds = session.context.duration * 60 - session.context.remaining;

  // Who else is focusing in this world. This browser announces itself only
  // while in a session; everyone else sees it fill one of the shared seats.
  const { peers, serverOffset } = usePresence(activeScene.id, {
    active: phase === 'world',
    remaining: session.context.remaining,
    seatCount: activeScene.seats.length,
    focusedSeconds,
    running: session.isRunning,
    tz: sharePlace ? myTz : undefined,
  });
  const serverNow = Date.now() + serverOffset;
  const { seated } = arrangeSeats(peers, activeScene.seats.length);
  const occupiedSeats = new Set(seated.keys());
  const seatFocus = new Map([...seated].map(([seat, p]) => [seat, focusedMinutes(p, serverNow)]));
  const seatWhere = new Map([...seated].map(([seat, p]) => [seat, whereLabel(p.tz, new Date(serverNow))]));
  const myMinutes = Math.floor(focusedSeconds / 60);

  // Arrivals and departures while you're in the world surface as a brief notice
  const [notice, setNotice] = useState<{ id: number; text: string } | null>(null);
  const seatingRef = useRef<Map<string, number> | null>(null);
  const inWorld = phase === 'world';

  useEffect(() => {
    const seating = seatsByUid(peers, activeScene.seats.length);
    const before = seatingRef.current;
    seatingRef.current = inWorld ? seating : null;
    if (!inWorld) return;

    const change = before ? diffSeating(before, seating) : null;
    if (change?.joined.length && chimeOnRef.current && Date.now() - lastChimeRef.current > CHIME_GAP_MS) {
      lastChimeRef.current = Date.now();
      playChime();
    }

    const text = change
      ? describeChange(
          change,
          (i) => activeScene.seats[i]?.label,
          (i) => cityOf(arrangeSeats(peers, activeScene.seats.length).seated.get(i)?.tz)
        )
      : peers.length
        ? `you're in · ${peers.length} ${peers.length === 1 ? 'other' : 'others'} here`
        : 'you have the café to yourself';
    if (text) setNotice({ id: Date.now(), text });
  }, [peers, inWorld]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Other people's focus time keeps moving while you're paused (when the timer
  // isn't re-rendering every second), so refresh it on a slow tick
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    if (!inWorld) return;
    const timer = setInterval(() => setFocusTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, [inWorld]);

  // Focus milestones (30 min, an hour, ...) crossed while you're watching, for
  // you and everyone else; times already passed when you arrive aren't announced
  const focusSeenRef = useRef(new Map<string, number>());
  useEffect(() => {
    const seen = focusSeenRef.current;
    if (!inWorld) {
      seen.clear();
      return;
    }
    let text: string | null = null;
    const check = (key: string, minutes: number, who: Parameters<typeof describeMilestone>[1]) => {
      const before = seen.get(key);
      seen.set(key, minutes);
      const milestone = before === undefined ? null : crossedMilestone(before, minutes);
      if (milestone) text = describeMilestone(milestone, who);
    };

    const seatOf = seatsByUid(peers, activeScene.seats.length);
    for (const p of peers) {
      const seat = seatOf.get(p.uid) ?? -1;
      check(p.uid, focusedMinutes(p, Date.now() + serverOffset), {
        label: activeScene.seats[seat]?.label,
        city: cityOf(p.tz),
      });
    }
    check('self', myMinutes, { self: true });
    if (text) setNotice({ id: Date.now(), text });
  }, [myMinutes, peers, inWorld, serverOffset, focusTick]);

  // The stage (backdrop + session video) stays mounted across every phase so
  // entering can dissolve straight into the session without the video reloading.
  return (
    <div className="stage" data-phase={phase}>
      <div
        className="stage-backdrop"
        style={{ backgroundImage: `url(${backdropImage})` }}
      />
      <div className="stage-window">
        {activeScene.ready ? (
          <SceneRenderer
            scene={activeScene}
            occupiedSeats={occupiedSeats}
            seatFocus={inWorld ? seatFocus : undefined}
            seatWhere={inWorld ? seatWhere : undefined}
            showHero={phase === 'entering' || phase === 'world' || phase === 'complete'}
            heroPaused={session.isPaused}
            heroFocus={inWorld ? myMinutes : undefined}
            lookId={lookId}
          />
        ) : (
          <WorldRenderer world={session.context.world} paused={session.isPaused} />
        )}
      </div>
      <div className="stage-grain" aria-hidden="true" />

      {(phase === 'lobby' || phase === 'entering') && (
        <Lobby
          session={session}
          player={player}
          loadSong={loadSong}
          leaving={phase === 'entering'}
          landingPage={landingPage}
          othersFocusing={peers.length}
          looks={activeScene.ready ? activeScene.looks : undefined}
          lookId={lookId}
          onPickLook={pickLook}
          myWhere={whereLabel(myTz)}
          sharePlace={sharePlace}
          onToggleSharePlace={toggleSharePlace}
        />
      )}

      {(phase === 'world' || phase === 'exiting') && (
        <World
          session={session}
          isExiting={phase === 'exiting'}
          player={player}
          othersFocusing={peers.length}
          notice={notice?.text}
          noticeId={notice?.id}
          chimeOn={chimeOn}
          onChimeToggle={toggleChime}
        />
      )}

      {phase === 'complete' && (
        <SessionComplete session={session} othersFocusing={peers.length} />
      )}
    </div>
  );
};

export default App;
